/**
 * Tally integration models — Phase 2.
 *
 * Property names mirror the backend view models (PmsEntity/ViewModel/TallyVm.cs)
 * exactly, PascalCase, so payloads round-trip without mapping.
 */

/** Single per-tenant Tally configuration row. AuthSecretName is a Key Vault reference name, never a secret value. */
export class TallyConfigModel {
  TallyConfigId = 0;
  Enabled = false;
  TransportMode = 'Disabled';
  TallyBaseUrl: string = null;
  TallyCompanyName: string = null;
  AuthSecretName: string = null;
  LastConnectionTestUtc: string = null;
  LastConnectionStatus: string = null;
  PurchaseVoucherType: string = null;
  /** Voucher type per purchased product type; null uses PurchaseVoucherType. */
  RawPurchaseVoucherType: string = null;
  FinishedPurchaseVoucherType: string = null;
  InternalUsePurchaseVoucherType: string = null;
  PushAsOptional = true;
  UseTallyVoucherNumbering = true;
  IncludeBillWiseDetails = true;
  RoundingMethod = 'Normal';
  AutoPushOnVerify = false;
  /** Where a hand push takes godowns when auto push is off: 'Store' (allocation, the default) or 'Department'. */
  GodownSource = 'Store';
  DefaultCurrency = 'INR';
  PurchaseLedgerName: string = null;
  CgstLedgerName: string = null;
  SgstLedgerName: string = null;
  IgstLedgerName: string = null;
  RoundOffLedgerName: string = null;
  FreightLedgerName: string = null;
  TdsLedgerName: string = null;
  DiscountLedgerName: string = null;
  OtherChargesLedgerName: string = null;
  SupplierSyncMode = 'Off';
  CustomerSyncMode = 'Off';
  StockSyncMode = 'Off';
  InventoryVoucherMode = false;
  /** Resolve purchase/tax ledgers per GST rate instead of the single names above. */
  UseRateBasedLedgers = false;
  SyncScheduleCron: string = null;
  MasterCacheTtlHours: number = null;
  LastLedgerSyncUtc: string = null;
  CreditEnforcementMode = 'Off';
  CreditStalenessToleranceMinutes: number = null;
  OverdueThresholdDays: number = null;
  EnforceOnSalesOrder = false;
  EnforceOnDispatch = false;
  AddedById: number = null;
  AddedDate: string = null;
  UpdatedById: number = null;
  UpdatedDate: string = null;
  UpdatedByName: string = null;
}

/** One audit-trail row of the configuration change history (newest first). */
export interface TallyConfigHistoryModel {
  TallyConfigHistoryId: number;
  TallyConfigId: number;
  ChangeType: string;
  /** JSON map of { FieldName: { Old, New } } — parsed client-side for the timeline. */
  ChangedFields: string;
  SnapshotJson: string;
  ChangedById: number | null;
  ChangedByName: string;
  ChangedDate: string;
  ChangeReason: string;
}

/** One goods receipt inside a supplier invoice's dashboard row. */
export interface TallyDashboardReceiptModel {
  InvoiceId: number;
  Grn: string;
  PoNumber: string;
  /** That receipt's own share of the invoice value. */
  InvoiceTotal: number | null;
}

/**
 * One Sync Dashboard row: a supplier invoice, the goods receipts it was booked
 * across, and its Tally sync state — one row per invoice, because one invoice
 * is one voucher in Tally however many receipts carried it.
 */
export interface TallyDashboardRowModel {
  /** The invoice's lowest receipt — the id the push and the preview both take. */
  InvoiceId: number;
  InvoiceNumber: string;
  InvoiceDate: string | null;
  SupplierName: string;
  /** Every GRN on the invoice, joined for display; Receipts carries them individually. */
  Grn: string;
  /** The whole invoice's value, summed across its receipts. */
  InvoiceTotal: number | null;
  /** True only when every receipt on the invoice is verified — the voucher is all of it. */
  IsVerified: boolean;
  SyncStatus: string;
  LastError: string;
  PushedDate: string | null;
  PostedDate: string | null;
  TallyVoucherNumber: string;
  /** The sync record behind this invoice; null when it has never been pushed. */
  TallyVoucherSyncId: number | null;
  /** The goods receipts this invoice was booked across — one per GRN, oldest first. */
  Receipts: TallyDashboardReceiptModel[];
  /**
   * Set when the receipts on this invoice no longer match what was sent to Tally.
   * The voucher cannot be amended from here, so this is a hand-off to Accounts.
   */
  DriftReason: string | null;
  /** Why auto push has not sent this invoice yet; null when nothing is held. */
  AutoPushHoldReason: string | null;
  /** Who sent the voucher: a user's name, or "Auto push" for the machine. */
  PushedByName: string | null;
  /**
   * Set when the invoice is deliberately outside the Tally push (import, knitting).
   * Group-wide: one excluded receipt excludes the whole invoice.
   */
  ExclusionReason: string | null;
  /**
   * Short tag text for an exclusion that has its own wording (free of charge);
   * null for import and knitting, which keep "Not sent to Tally".
   * Optional so specs that predate it still compile.
   */
  ExclusionLabel?: string | null;
  /**
   * Set when the voucher would post with an incomplete Party Details screen.
   * Shown as a warning; a missing GSTIN or state also blocks the push through
   * PushBlockReason.
   */
  PartyDetailWarning: string | null;
  /**
   * Why this invoice cannot be pushed right now, worked out by the server with the
   * same checks the push itself runs; null when it can be pushed. Only filled for
   * invoices not yet sent (Verified) or whose last push failed.
   */
  PushBlockReason?: string | null;
}

/** Which Tally master list a refresh reads. */
export type TallyMasterSyncKind = 'Ledgers' | 'Godowns' | 'StockItems';

/** How long refresh history is kept (the server deletes older runs and never lists them). */
export const TALLY_SYNC_HISTORY_DAYS = 30;

/**
 * One refresh of a Tally master list. DisplayStatus and IsActive come from the
 * server, which also turns a run nobody answered into "No answer".
 */
export interface TallyMasterSyncRunModel {
  TallyMasterSyncRunId: number;
  SyncKind: TallyMasterSyncKind;
  /** Queued | Dispatched | Completed | Failed, as stored. */
  Status: string;
  /** Waiting for the Tally agent | Reading from Tally | Completed | Failed | No answer. */
  DisplayStatus: string;
  /** True while the screen should keep polling. */
  IsActive: boolean;
  RequestedUtc: string;
  DispatchedUtc: string | null;
  CompletedUtc: string | null;
  ItemCount: number | null;
  Detail: string | null;
  RequestedByName: string | null;
  /** True for a manual Full refresh, which re-reads every master. Absent on an older backend. */
  IsFullSync?: boolean;
}

/** The two lists a manual Full refresh can re-read (godowns are always a single read). */
export type TallyFullSyncKind = 'Ledgers' | 'StockItems';

/**
 * What one item of each Full-refresh list is called, for the confirmation and messages.
 */
export const TALLY_FULL_SYNC_NOUN: { [kind in TallyFullSyncKind]: string } = {
  Ledgers: 'ledger',
  StockItems: 'stock item'
};

/**
 * The Tally connection as the Sync Dashboard sees it. Its own read, gated on the
 * dashboard permission, so a user who may push but not configure still sees
 * whether Tally is there to receive the voucher.
 */
export interface TallyHostStatusModel {
  Enabled: boolean;
  TransportMode: string;
  /** Off: the supplier invoice number is sent as the voucher number. */
  UseTallyVoucherNumbering: boolean;
  /** Connected | CompanyMismatch | Unreachable, from the last connection test. */
  LastConnectionStatus: string | null;
  LastConnectionTestUtc: string | null;
  LastConnectionDetail: string | null;
  /** When the on-site agent last checked in; null when no agent ever has. */
  AgentLastHeartbeatUtc: string | null;
  /** The agent's own probe of Tally at its last check-in; null when unknown. */
  AgentTallyReachable: boolean | null;
  AgentTallyStatusDetail: string | null;
  AgentTallyStatusUtc: string | null;
  /** Minutes since the agent's last heartbeat, worked out by the server; null when it never checked in. */
  AgentHeartbeatAgeMinutes: number | null;
  /**
   * Connected | CompanyNotOpen | Unreachable from the agent's last reading; null until an
   * agent that reports who runs Tally has checked in (older agents leave it null).
   */
  HostState?: string | null;
  /** When Tally entered HostState, as near as the agent's checks can tell. */
  HostStateSinceUtc?: string | null;
  HostCheckedUtc?: string | null;
  /** Minutes since HostCheckedUtc, worked out by the server. */
  HostCheckedAgeMinutes?: number | null;
  /** One plain line on the reading, naming the machine and users when known. */
  HostDetail?: string | null;
  /** The last reading with the configured company open, and who had it open. */
  LastServedUtc?: string | null;
  LastServedWindowsUser?: string | null;
  LastServedTallyUser?: string | null;
  LastServedMachineName?: string | null;
}

/** How many invoices a dashboard page holds unless the user picks otherwise. */
export const TALLY_DASHBOARD_PAGE_SIZE = 10;
export const TALLY_DASHBOARD_PAGE_SIZES: number[] = [10, 20, 50, 100];

/** One page of the dashboard: the invoices on it and how many match in all. */
export interface TallyDashboardPageModel {
  Items: TallyDashboardRowModel[];
  TotalCount: number;
  PageNumber: number;
  PageSize: number;
  /**
   * Verified and Needs details are checked newest first only until the page and one
   * more invoice are found; when true, TotalCount is a floor that just shows a next page.
   */
  TotalIsPartial?: boolean;
}

/**
 * The dashboard's filters, sent to the server, which counts and pages what matches.
 * Blank means no constraint. Dates are whole days, inclusive.
 */
export class TallyDashboardFilterModel {
  /** Which date the range applies to: the invoice's, or when its voucher was pushed. */
  DateType: 'Invoice' | 'Pushed' = 'Invoice';
  FromDate: Date | null = null;
  ToDate: Date | null = null;
  SupplierId: number | null = null;
  InvoiceNumber = '';
  Grn = '';
  PoNumber = '';
  /** 'auto' for the machine, a UserId as text for a person, null for anyone. */
  PushedBy: string | null = null;
}

/** One Pushed By choice: 'auto' or a UserId as text, with the name to show. */
export interface TallyPusherModel {
  Value: string;
  Name: string;
}

/** One GST rate's Tally ledger set. */
export interface TallyGstRateLedgerModel {
  TallyGstRateLedgerId: number;
  GstRate: number;
  PurchaseLedgerName: string | null;
  CgstLedgerName: string | null;
  SgstLedgerName: string | null;
  IgstLedgerName: string | null;
  /** Blank falls back to the voucher type on the main Tally configuration. */
  PurchaseVoucherType: string | null;
  IsActive: boolean;
}

/** One department's Tally godown, read for the unmapped count. Mapped on the Department master. */
export interface TallyDepartmentGodownModel {
  DeptId: number;
  DeptName: string;
  TallyGodownName: string | null;
  /** Whether this department raised a purchase order in the last year. */
  HasPurchases: boolean;
}

/** One human-readable posting line of a previewed voucher. */
export interface TallyVoucherPreviewLineModel {
  LedgerName: string;
  /** 'Debit' or 'Credit'. */
  Type: string;
  Amount: number;
}

/** One ERP measurement unit → Tally unit mapping. A blank TallyUnitName on save removes the mapping. */
export interface TallyUnitMappingModel {
  TallyUnitMappingId: number;
  ErpUnit: string;
  TallyUnitName: string | null;
}

/** One stock-item line of an item-invoice voucher preview. */
export interface TallyVoucherItemLineModel {
  StockItemName: string;
  /** The supplier's wording for the line; carried to Tally as the item's additional description. */
  Description: string | null;
  Quantity: number;
  Unit: string;
  Rate: number | null;
  Amount: number;
}

/** Voucher preview: the exact XML that would be pushed plus readable lines and warnings. */
export interface TallyVoucherPreviewModel {
  InvoiceId: number;
  VoucherXml: string;
  IsOptional: boolean;
  Lines: TallyVoucherPreviewLineModel[];
  /** Present only when the company pushes item-invoice vouchers. */
  ItemLines: TallyVoucherItemLineModel[] | null;
  Warnings: string[];
}

/** Body of POST tally/push. */
export interface TallyPushRequestModel {
  InvoiceId: number;
}

/** Body of POST tally/cleardrift. */
export interface TallyClearDriftRequestModel {
  TallyVoucherSyncId: number;
}

/** Push result. ManualXml is populated only for the ManualXml transport — the client downloads it. */
export interface TallyPushResultModel {
  /** The merged invoice's lowest receipt, which need not be the row that was clicked. */
  InvoiceId: number;
  Status: string;
  ManualXml: string;
  Message: string;
}

/** One parsed entry of the ChangedFields JSON, flattened for the history timeline. */
export interface TallyConfigFieldChange {
  Field: string;
  Old: string;
  New: string;
}

/**
 * Saved outcome of the most recent connectivity test (GET tally/getconnectionstatus).
 * The test is asynchronous — the on-premise agent picks it up on its next poll — so
 * completion is detected by LastConnectionTestUtc moving past its pre-test value.
 */
export interface TallyConnectionStatusModel {
  LastConnectionTestUtc: string | null;
  /** 'Connected' | 'CompanyMismatch' | 'Unreachable', or null when never tested. */
  LastConnectionStatus: string | null;
  /** Operator-facing one-liner explaining the status; shown as the health chip's tooltip. */
  LastConnectionDetail: string | null;
  /** Company names seen open in Tally on the last successful contact; empty when never tested. */
  OpenCompanies: string[];
}

// ── Allowed values (mirror the database CHECK constraints) ───────────────────

export const TALLY_TRANSPORT_MODES: string[] = ['Disabled', 'ManualXml', 'PollingAgent', 'HcmBridge'];
export const TALLY_ROUNDING_METHODS: string[] = ['Normal', 'Upward', 'Downward'];
export const TALLY_SYNC_MODES: string[] = ['Off', 'TallyIsMaster', 'ErpIsMaster', 'TwoWay'];
export const TALLY_CREDIT_ENFORCEMENT_MODES: string[] = ['Off', 'WarnOnly', 'BlockWithOverride', 'HardBlock'];

/** Sync statuses surfaced on the dashboard, in lifecycle order. */
export const TALLY_SYNC_STATUSES: string[] = ['Verified', 'Pending', 'PushedOptional', 'Posted', 'Failed', 'Duplicate'];

/** How a sync status reads on screen; a status not listed here reads as itself. */
export const TALLY_SYNC_STATUS_LABELS: Record<string, string> = {
  PushedOptional: 'Pushed (Optional)'
};

export function tallySyncStatusLabel(status: string): string {
  return TALLY_SYNC_STATUS_LABELS[status] ?? status;
}
